<div class="dashboard-page">
  <!-- Page Header -->
  <div class="mb-4">
    <h1 class="h2 mb-1 fw-bold">{{ pageTitle }}</h1>
    <p class="text-muted mb-0">{{ isEditMode ? 'Update your company information' : 'Set up your company profile to get started' }}</p>
  </div>

  <!-- Company Profile Form -->
  <div class="row">
    <div class="col-12 col-lg-8">
      <form [formGroup]="companyForm" (ngSubmit)="onSubmit()">

        <!-- Company Information Card -->
        <div class="card mb-3">
          <div class="card-header p-3">
            <h5 class="mb-0">Company Information</h5>
          </div>
          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12">
                <label for="name" class="form-label fw-medium">Company Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="name"
                  formControlName="name"
                  [class.is-invalid]="companyForm.get('name')?.invalid && companyForm.get('name')?.touched"
                  placeholder="Enter company name"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('name')?.invalid && companyForm.get('name')?.touched">
                  Company name is required
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Owner Information Card (Read-only) -->
        <div class="card mb-3">
          <div class="card-header p-3">
            <h5 class="mb-0">Owner Information</h5>
          </div>
          <div class="card-body p-4">
            <p class="small text-muted mb-3">The logged-in user will be set as the owner of this organization.</p>
            <div class="row">
              <div class="col-12 col-md-6">
                <div class="mb-0">
                  <span class="small text-muted d-block">Name</span>
                  <span class="fw-medium">{{ ownerDisplayName || '—' }}</span>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="mb-0">
                  <span class="small text-muted d-block">Email</span>
                  <span class="fw-medium">{{ ownerEmail || '—' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tax Identification Card -->
        <div class="card mb-3">
          <div class="card-header p-3">
            <h5 class="mb-0">Tax Identification</h5>
          </div>
          <div class="card-body p-4">
            <p class="small text-muted mb-3">Required for 1099 tax reporting. This information is encrypted and stored securely.</p>
            <div class="row g-3">
              <div class="col-12">
                <label for="organizationType" class="form-label fw-medium">Organization Type <span class="text-danger">*</span></label>
                <select
                  class="form-select"
                  id="organizationType"
                  formControlName="organizationType"
                  [class.is-invalid]="companyForm.get('organizationType')?.invalid && companyForm.get('organizationType')?.touched"
                >
                  <option value="">Select Organization Type</option>
                  <option *ngFor="let type of organizationTypes" [value]="type.value">
                    {{ type.label }}
                  </option>
                </select>
                <div class="invalid-feedback" *ngIf="companyForm.get('organizationType')?.invalid && companyForm.get('organizationType')?.touched">
                  Organization type is required
                </div>
              </div>

              <div class="col-12 col-md-6" *ngIf="hasOrganizationType && !isIndividual">
                <label for="ein" class="form-label fw-medium">EIN <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="ein"
                  formControlName="ein"
                  [class.is-invalid]="companyForm.get('ein')?.invalid && companyForm.get('ein')?.touched"
                  placeholder="12-3456789"
                  mask="00-0000000"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('ein')?.invalid && companyForm.get('ein')?.touched">
                  EIN is required
                </div>
              </div>

              <div class="col-12 col-md-6" *ngIf="isIndividual">
                <label for="ssn" class="form-label fw-medium">SSN <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="ssn"
                  formControlName="ssn"
                  [class.is-invalid]="companyForm.get('ssn')?.invalid && companyForm.get('ssn')?.touched"
                  placeholder="123-45-6789"
                  mask="000-00-0000"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('ssn')?.invalid && companyForm.get('ssn')?.touched">
                  SSN is required
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Address Card -->
        <div class="card mb-3">
          <div class="card-header p-3">
            <h5 class="mb-0">Business Address</h5>
          </div>
          <div class="card-body p-4">
            <div class="row g-3">
              <div class="col-12">
                <label for="streetAddressLine1" class="form-label fw-medium">Street Address <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="streetAddressLine1"
                  formControlName="streetAddressLine1"
                  [class.is-invalid]="companyForm.get('streetAddressLine1')?.invalid && companyForm.get('streetAddressLine1')?.touched"
                  placeholder="123 Main Street"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('streetAddressLine1')?.invalid && companyForm.get('streetAddressLine1')?.touched">
                  Street address is required
                </div>
              </div>

              <div class="col-12">
                <label for="streetAddressLine2" class="form-label fw-medium">Street Address Line 2</label>
                <input
                  type="text"
                  class="form-control"
                  id="streetAddressLine2"
                  formControlName="streetAddressLine2"
                  placeholder="Suite, Unit, Building, etc."
                >
              </div>

              <div class="col-12 col-md-6">
                <label for="city" class="form-label fw-medium">City <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="city"
                  formControlName="city"
                  [class.is-invalid]="companyForm.get('city')?.invalid && companyForm.get('city')?.touched"
                  placeholder="Enter city"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('city')?.invalid && companyForm.get('city')?.touched">
                  City is required
                </div>
              </div>

              <div class="col-12 col-md-3">
                <label for="state" class="form-label fw-medium">State <span class="text-danger">*</span></label>
                <select
                  class="form-select"
                  id="state"
                  formControlName="state"
                  [class.is-invalid]="companyForm.get('state')?.invalid && companyForm.get('state')?.touched"
                >
                  <option value="">Select State</option>
                  <option value="AL">Alabama</option>
                  <option value="AK">Alaska</option>
                  <option value="AZ">Arizona</option>
                  <option value="AR">Arkansas</option>
                  <option value="CA">California</option>
                  <option value="CO">Colorado</option>
                  <option value="CT">Connecticut</option>
                  <option value="DE">Delaware</option>
                  <option value="FL">Florida</option>
                  <option value="GA">Georgia</option>
                  <option value="HI">Hawaii</option>
                  <option value="ID">Idaho</option>
                  <option value="IL">Illinois</option>
                  <option value="IN">Indiana</option>
                  <option value="IA">Iowa</option>
                  <option value="KS">Kansas</option>
                  <option value="KY">Kentucky</option>
                  <option value="LA">Louisiana</option>
                  <option value="ME">Maine</option>
                  <option value="MD">Maryland</option>
                  <option value="MA">Massachusetts</option>
                  <option value="MI">Michigan</option>
                  <option value="MN">Minnesota</option>
                  <option value="MS">Mississippi</option>
                  <option value="MO">Missouri</option>
                  <option value="MT">Montana</option>
                  <option value="NE">Nebraska</option>
                  <option value="NV">Nevada</option>
                  <option value="NH">New Hampshire</option>
                  <option value="NJ">New Jersey</option>
                  <option value="NM">New Mexico</option>
                  <option value="NY">New York</option>
                  <option value="NC">North Carolina</option>
                  <option value="ND">North Dakota</option>
                  <option value="OH">Ohio</option>
                  <option value="OK">Oklahoma</option>
                  <option value="OR">Oregon</option>
                  <option value="PA">Pennsylvania</option>
                  <option value="RI">Rhode Island</option>
                  <option value="SC">South Carolina</option>
                  <option value="SD">South Dakota</option>
                  <option value="TN">Tennessee</option>
                  <option value="TX">Texas</option>
                  <option value="UT">Utah</option>
                  <option value="VT">Vermont</option>
                  <option value="VA">Virginia</option>
                  <option value="WA">Washington</option>
                  <option value="WV">West Virginia</option>
                  <option value="WI">Wisconsin</option>
                  <option value="WY">Wyoming</option>
                </select>
                <div class="invalid-feedback" *ngIf="companyForm.get('state')?.invalid && companyForm.get('state')?.touched">
                  State is required
                </div>
              </div>

              <div class="col-12 col-md-3">
                <label for="zipCode" class="form-label fw-medium">ZIP Code <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="zipCode"
                  formControlName="zipCode"
                  [class.is-invalid]="companyForm.get('zipCode')?.invalid && companyForm.get('zipCode')?.touched"
                  placeholder="12345"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('zipCode')?.invalid && companyForm.get('zipCode')?.touched">
                  ZIP code is required
                </div>
              </div>
            </div>
          </div>
          <div class="px-3 mt-3 d-flex justify-content-between align-items-center">
            <h6 class="mb-0">Coordinates</h6>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary mt-3"
              (click)="lookupCoordinates()">
              <i class="bi bi-crosshair me-1"></i>
              Look up
            </button>
          </div>
          <div class="card-body">
            <!-- Lookup error -->
            <div *ngIf="getCoordinatesError" class="alert alert-danger d-flex align-items-start mb-3" role="alert">
              <i class="bi bi-x-circle-fill me-2 mt-1"></i>
              <div>
                <div class="fw-semibold">Coordinate Lookup failed</div>
                <div class="small">{{ getCoordinatesError }}</div>
                <div class="small">Update the address or enter coordinates manually below</div>
              </div>
            </div>
            <div class="row">
              <!-- Latitude -->
              <div class="col-md-6 mb-3">
                <label for="latitude" class="form-label">Latitude</label>
                <input
                  type="text"
                  class="form-control"
                  id="latitude"
                  formControlName="latitude"
                  placeholder="37.7749"
                  [class.is-invalid]="companyForm.get('latitude')?.invalid && companyForm.get('latitude')?.touched"
                
                />
                <div *ngIf="companyForm.get('latitude')?.invalid && companyForm.get('latitude')?.touched" class="invalid-feedback">
                  Please enter a valid latitude
                </div>
              </div>
              <!-- Longitude -->
              <div class="col-md-6 mb-3">
                <label for="longitude" class="form-label">Longitude</label>
                <input
                  type="text"
                  class="form-control"
                  id="longitude"
                  formControlName="longitude"
                  placeholder="-122.4194"
                  [class.is-invalid]="companyForm.get('longitude')?.invalid && companyForm.get('longitude')?.touched"
                />
                <div *ngIf="companyForm.get('longitude')?.invalid && companyForm.get('longitude')?.touched" class="invalid-feedback">
                  Please enter a valid longitude
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Information Card (Optional) -->
        <div class="card mb-4">
          <div class="card-header p-3">
            <h5 class="mb-0">Contact Information (Optional)</h5>
          </div>
          <div class="card-body p-4">
            <p class="small text-muted mb-3">Optional business contact information separate from the owner's contact details.</p>
            <div class="row g-3">
              <div class="col-12 col-md-6">
                <label for="email" class="form-label fw-medium">Business Email</label>
                <input
                  type="email"
                  class="form-control"
                  id="email"
                  formControlName="email"
                  [class.is-invalid]="companyForm.get('email')?.invalid && companyForm.get('email')?.touched"
                  placeholder="info@company.com"
                >
                <div class="invalid-feedback" *ngIf="companyForm.get('email')?.invalid && companyForm.get('email')?.touched">
                  Please enter a valid email address
                </div>
              </div>

              <div class="col-12 col-md-6">
                <label for="phone" class="form-label fw-medium">Business Phone</label>
                <input
                  type="tel"
                  class="form-control"
                  id="phone"
                  formControlName="phone"
                  placeholder="(555) 123-4567"
                  mask="(000) 000-0000"
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div class="alert alert-danger" *ngIf="errorMessage">
          {{ errorMessage }}
        </div>

        <!-- Form Actions -->
        <div class="d-flex gap-3 mb-4">
          <button
            type="submit"
            class="btn btn-primary"
            [disabled]="isLoading"
          >
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            {{ submitButtonText }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            (click)="onCancel()"
            [disabled]="isLoading"
          >
            Cancel
          </button>
        </div>

      </form>
    </div>

    <!-- Sidebar with helpful information -->
    <div class="col-12 col-lg-4">
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Why do we need this?</h5>
        </div>
        <div class="card-body p-4">
          <div class="d-flex flex-column gap-3">
            <div>
              <h6 class="fw-bold mb-2">
                <i class="bi bi-building me-2 text-primary"></i>
                Company Profile
              </h6>
              <p class="small text-muted mb-0">
                Your company information helps us provide better service and ensures compliance with regulations.
              </p>
            </div>

            <div>
              <h6 class="fw-bold mb-2">
                <i class="bi bi-receipt me-2 text-primary"></i>
                Tax Reporting
              </h6>
              <p class="small text-muted mb-0">
                EIN or SSN is required for 1099 tax reporting. This information is encrypted and stored securely.
              </p>
            </div>

            <div>
              <h6 class="fw-bold mb-2">
                <i class="bi bi-shield-check me-2 text-primary"></i>
                Secure & Private
              </h6>
              <p class="small text-muted mb-0">
                All your information is encrypted and stored securely. We never share your data without permission.
              </p>
            </div>

            <div>
              <h6 class="fw-bold mb-2">
                <i class="bi bi-people me-2 text-primary"></i>
                Team Management
              </h6>
              <p class="small text-muted mb-0">
                Once your company is set up, you can invite team members and manage permissions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- coordinates lookup confirmation modal that shows the found coordinates plus a map  -->
<ng-container *ngIf="pendingCoordinates">
  <div class="modal-backdrop fade show" (click)="declinedCoordinates()"></div>
  <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
    <div class="modal-dialog modal-dialog-centered modal-lg w-100" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-geo-alt me-2"></i>Confirm Coordinates</h5>
          <button type="button" class="btn-close" (click)="declinedCoordinates()" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <p class="text-body-secondary small mb-3">
            Found from: {{ companyForm?.get('streetAddressLine1')?.value }}, {{ companyForm?.get('city')?.value }}, {{ companyForm?.get('state')?.value }} {{ companyForm?.get('zipCode')?.value }}
          </p>

          <div class="row g-3 mb-3">
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Current</label>
              <div *ngIf="companyForm?.get('latitude')?.value != null; else notSet">
                {{ companyForm?.get('latitude')?.value | number:'1.5-5' }}, {{ companyForm?.get('longitude')?.value | number:'1.5-5' }}
              </div>
              <ng-template #notSet><div class="text-body-secondary fst-italic">Not set</div></ng-template>
            </div>
            <div class="col-sm-6">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">New</label>
              <div class="fw-semibold">
                {{ pendingCoordinates.lat | number:'1.5-5' }}, {{ pendingCoordinates.lng | number:'1.5-5' }}
              </div>
            </div>
          </div>

          <div class="rounded-2 border overflow-hidden">
            <google-map
              [center]="pendingCoordinates"
              [zoom]="15"
              [options]="mapOptions"
              width="100%"
              height="320px">
              <map-marker [position]="pendingCoordinates"></map-marker>
            </google-map>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="declinedCoordinates()">
            Decline
          </button>
          <button type="button" class="btn btn-primary" (click)="confirmedCoordinates()">
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-container>
